<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { handleBackNavigation } from '@/helpers/navigation';
import RowItem, { RowItemProps } from '@/molecules/RowItem.vue';
import TitleHeader from '@/molecules/TitleHeader.vue';
import HighlightBox from '@/molecules/HighlightBox.vue';
import { useAppModal } from '@/composables/useAppModal';
import RowItemContainer from '@/organisms/RowItemContainer.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import { buildRisks } from '@/parcours/flows/stress/riskStress.config';

defineEmits(['back', 'next', 'submit', 'canSubmit']);

const route = useRoute();
const router = useRouter();

type ConfigItem = {
  key: string;
  label: string;
  type?: string;
  showIf?: (data: any) => boolean;
};

type ConfigCategory = {
  title: string;
  description?: string;
  items: ConfigItem[];
  actions: Action[];
};

type Action = {
  id?: string;
  label: string;
  onClick: Function;
};

const props = defineProps<{
  data: Record<string, any>;
  config: Record<string, ConfigCategory>;
}>();

const modal = useAppModal();

const symptom = computed(() => route.query.symptom as string);
const category = computed(() => props.config?.[symptom.value]);

const currentRisk = computed(() => buildRisks(props.data).find((r) => r.label === symptom.value));
const scoreLabel = computed(() => {
  const risk = currentRisk.value;
  if (!risk || risk.level == null) return null;
  return `${risk.level} / ${risk.total}`;
});
const scoreColor = computed(() => {
  const risk = currentRisk.value;
  if (!risk || risk.level == null) return 'error';
  if (risk.level < risk.min) return 'success';
  if (risk.level < risk.max) return 'warning';
  return 'error';
});

async function goToShowMore(riskLabel: string) {
  await router.push({
    name: `flow-${route.meta.flowId}-symptomAnswerQuestion`,
    params: route.params,
    query: { symptom: riskLabel },
  });
}

const stepActionsItems = computed<RowItemProps[]>(() => {
  const actions = category.value?.actions ?? [];
  return actions.map((action) => ({
    title: action.label,
    id: action.id ?? action.label,
    class: 'py-7',
    onClick: () => action.onClick({ modal, data: null, request: null, router }),
  }));
});
</script>

<template>
  <div>
    <TitleHeader
      :description="category?.description"
      :on-back="() => handleBackNavigation()"
      :tabs="[]"
      :title="category?.title || '-'"
    />

    <HighlightBox v-if="scoreLabel" class="mb-6 mt-6" :value="scoreLabel" :color="scoreColor" />

    <RowItem
      id="questions"
      :icon="{ name: 'user', background: 'brand', size: '32' }"
      class="border rounded-xl"
      description="Questions répondus"
      title="Questions"
      @click="goToShowMore(symptom)"
    >
      <LocalIcon name="chevron-right" />
    </RowItem>

    <div v-if="category?.actions?.length" class="mt-4">
      <RowItemContainer :row-items="stepActionsItems" row-item-type="RowItem" type="primary">
        <LocalIcon name="chevron-right" />
      </RowItemContainer>
    </div>
  </div>
</template>
